/*
 * @providesModule RESTStore
 * @noflow
 *
   RESTStore is a simple cache of REST HTTP responses

   Supports "two phase" GETs in which responses are returned first
   from the cache and then from the server
*/
import AsyncStorage from '@react-native-async-storage/async-storage';
import ErrorDialogModal from 'ErrorDialogModal';
import GlobalEventEmitter from 'GlobalEventEmitter';
import ModalNavigationController from 'ModalNavigationController';
import NetClient from 'NetClient';
import _ from 'lodash';
import React from 'react';

import {Platform, AppState, NativeModules, AppStateStatus} from 'react-native';
import DdLogs from 'DdLogsWrapper';
import {getErrorMessage} from 'DataDogErrorUtils';
import ClientPropertyConstants from 'ClientPropertyConstants';

const SkillzModule = NativeModules.SkillzModule;

const CACHE_PREFIX = 'SKILLZ_URL_CACHE_';

export type RetryMode = 'failSilently' | 'promptRetry' | 'majorError' | 'simpleError';

// Grace period (in ms) after app comes to foreground during which errors are suppressed
// This covers the time needed for background refresh API calls to complete
const BACKGROUND_RESUME_GRACE_PERIOD_MS = 5000; // 5 seconds

// Track when app last transitioned from background to foreground
let lastForegroundTimestamp: number | null = null;
let previousAppState: AppStateStatus = AppState.currentState;

// Listen for app state changes to track background→foreground transitions
AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
  if (previousAppState.match(/inactive|background/) && nextAppState === 'active') {
    // App just came to foreground from background
    lastForegroundTimestamp = Date.now();
    console.debug('RESTStore: App transitioned to foreground, starting grace period');
  }
  previousAppState = nextAppState;
});

/**
 * Checks if the app recently transitioned from background to foreground
 * @returns true if within the grace period after background→foreground transition
 */
export const isWithinBackgroundResumeGracePeriod = (): boolean => {
  if (lastForegroundTimestamp === null) {
    return false;
  }
  const timeSinceForegrounded = Date.now() - lastForegroundTimestamp;
  return timeSinceForegrounded < BACKGROUND_RESUME_GRACE_PERIOD_MS;
};

// Error codes that should be handled silently without showing error dialogs
// These are typically transient errors that occur during network reconnection
// or auth token refresh scenarios (e.g., app resuming from background)
const SILENT_ERROR_CODES = [
  401, // Unauthorized - should trigger re-auth flow, not error dialog
  408, // Request Timeout - transient, retry silently
  502, // Bad Gateway - server issue, transient
  503, // Service Unavailable - server issue, transient
  504, // Gateway Timeout - server issue, transient
];

// Network error codes (negative values are iOS NSURLError codes)
const NETWORK_ERROR_CODES = [
  -1001, // NSURLErrorTimedOut
  -1009, // NSURLErrorNotConnectedToInternet
  -1004, // NSURLErrorCannotConnectToHost
  -1005, // NSURLErrorNetworkConnectionLost
  -1020, // NSURLErrorDataNotAllowed
];

/**
 * Checks if an error should be handled silently without showing a dialog
 * Only suppresses errors during background→foreground transition grace period
 * @param error - The error object from the API call
 * @returns true if the error should be handled silently
 */
const shouldHandleErrorSilently = (error: any): boolean => {
  // Feature flag check - if disabled, never suppress error dialogs
  if (!ClientPropertyConstants.getSuppressUhOhDialog()) {
    return false;
  }

  // Only suppress errors during background→foreground grace period
  if (!isWithinBackgroundResumeGracePeriod()) {
    return false;
  }

  // Get error message to check for specific error types
  const errorMessage = getErrorMessage(error);
  const errorMessageLower = errorMessage?.toLowerCase() || '';

  // NEVER suppress malformed request errors - these indicate bugs in the code
  // e.g., "tournaments/undefined" suggests undefined tournamentId parameter
  if (errorMessageLower.includes('malformed') || errorMessageLower.includes('malformed_requst')) {
    console.debug(`RESTStore: NOT suppressing malformed request error (message: ${errorMessage})`);
    return false;
  }

  // Suppress "Network request failed" errors - these are transient network issues
  if (errorMessageLower.includes('network request failed')) {
    console.debug(
      `RESTStore: Handling network request failed error silently during background resume (message: ${errorMessage})`
    );
    return true;
  }

  // Get error code from various possible locations
  const errorCode =
    error?.code || error?.status || error?.responseObject?.code || error?.response?.status;

  if (!errorCode) {
    return false;
  }

  // Check if it's a silent error code
  if (SILENT_ERROR_CODES.includes(errorCode)) {
    console.debug(
      `RESTStore: Handling error silently during background resume (code: ${errorCode})`
    );
    return true;
  }

  // Check if it's a network error
  if (NETWORK_ERROR_CODES.includes(errorCode)) {
    console.debug(
      `RESTStore: Handling network error silently during background resume (code: ${errorCode})`
    );
    return true;
  }

  return false;
};

const RESTStore = {
  // This function implements a "cache-only" policy for get response
  // Returns a cached response if one exists - and never returns a response form the server
  // note that the client supplied callback will be invoked with null if no cached response is found
  getCachedResponse: function (url: string, callback) {
    console.debug('RS Cache: Attempt cache response fetch: ' + this._cacheNameForURL(url));
    AsyncStorage.getItem(this._cacheNameForURL(url), (error, item) => {
      if (!error) {
        if (!item) {
          if (callback) {
            console.debug('RS Cache: No cache, no error ' + url);
            callback(null, null);
          }
          return;
        }
        try {
          const response = JSON.parse(item);
          if (callback) {
            console.debug('RS Cache: cache response found ' + url);
            callback(null, response);
          }
        } catch (errorParse) {
          console.debug(
            'RS Cache: cache response found, error parse:' + errorParse + ' url:' + url
          );
          callback(errorParse, null);
        }
      } else {
        console.debug('RS Cache: error on cache fetch: ' + error + ' url: ' + url);
        callback(error, null);
      }
    });
  },

  async updateLocalStorage(url, responseJson) {
    if (responseJson && url) {
      await AsyncStorage.setItem(this._cacheNameForURL(url), JSON.stringify(responseJson));
      GlobalEventEmitter.emit('localStorageUpdated', url);
    }
  },

  /**
   * Convenience method used to conditionally use the cache
   *
   * This enables callers to not have to locally branch on which RESTStore
   * method to call
   */
  async getResponseWithOptions<T>(
    url: string,
    params: Record<string, string> | null,
    retryMode: RetryMode,
    {callback, useCache}: {callback?: (response: any, error: null) => void; useCache?: boolean}
  ) {
    if (useCache) {
      console.debug('RS Cache: getResponseWithOptions useCache: ' + url);
      return this.getAnyResponse(url, params, retryMode, callback);
    }
    console.debug('RS Cache: getResponseWithOptions no cache: ' + url);
    return this.getServerResponse(url, params, retryMode, callback);
  },

  // This function implements a "server-only" policy for get response
  // Returns a response from the server - and never returns any responses from the cache
  async getServerResponse<T>(
    url: string,
    params: Record<string, string> | null,
    retryMode: RetryMode,
    callback?: (response: any, error: null) => void
  ) {
    let responseJson = null;
    try {
      responseJson = await NetClient.getURL(url, params, retryMode);
      await this.updateLocalStorage(url, responseJson);
      if (!!callback) {
        callback(responseJson, null);
      }
    } catch (e) {
      console.debug('RS Cache: getServerResponse Error: ' + JSON.stringify(e) + ' url: ' + url);

      // Log error to Datadog with actual native shouldInteractWithInterface flag
      // Flatten all fields to avoid [object Object] in Datadog
      const currentAppState = AppState.currentState;
      const errorMessage = getErrorMessage(e);
      const errorCode = (e as any)?.code ?? null;
      const errorStatusCode = (e as any)?.statusCode ?? (e as any)?.status ?? null;

      const errorInfo = {
        url,
        retryMode,
        platform: Platform.OS,
        appState: currentAppState,
        timestamp: new Date().toISOString(),
        errorMessage,
        errorCode,
        errorStatusCode,
      };

      // Get actual native shouldInteractWithInterface value
      if (Platform.OS === 'ios' && SkillzModule?.shouldInteractWithInterface) {
        SkillzModule.shouldInteractWithInterface(
          (shouldInteract: boolean, isMatchInProgress: boolean) => {
            DdLogs.warn('[RESTSTORE_API_ERROR]', {
              ...errorInfo,
              shouldInteractWithInterface: shouldInteract,
              isMatchInProgress,
            });
          }
        );
      } else {
        DdLogs.warn('[RESTSTORE_API_ERROR]', {
          ...errorInfo,
          shouldInteractWithInterface: null,
        });
      }
      // Check if error should be handled silently (transient errors, background resume, etc.)
      const handleSilently = shouldHandleErrorSilently(e);

      // Track silent error suppression for observability
      const isWithinGracePeriod = isWithinBackgroundResumeGracePeriod();
      if (handleSilently) {
        DdLogs.info('[UH_OH_ERROR_SUPPRESSED]', {
          url,
          errorCode,
          errorStatusCode,
          errorMessage,
          appState: currentAppState,
          platform: Platform.OS,
          retryMode,
          isWithinGracePeriod,
          timestamp: new Date().toISOString(),
        });
      }

      if (
        !handleSilently &&
        Platform.OS === 'android' &&
        retryMode &&
        retryMode !== 'failSilently'
      ) {
        // Track error dialog display for observability
        DdLogs.warn('[UH_OH_ERROR_DIALOG_SHOWN]', {
          url,
          errorCode,
          errorStatusCode,
          errorMessage,
          platform: Platform.OS,
          isWithinGracePeriod,
          timestamp: new Date().toISOString(),
        });

        const modalProps = {
          animationType: 'fade',
          transparent: true,
        };
        const errorDialogMessage = this._getMessageForError(e);
        const errorDialog = (
          <ErrorDialogModal
            message={errorDialogMessage}
            retryCallback={() => {
              ModalNavigationController.hideModal();
              this.getServerResponse(url, params, retryMode, callback);
            }}
            onDone={() => {
              ModalNavigationController.hideModal();
            }}
            error={e}
          />
        );

        ModalNavigationController.showModal(errorDialog, modalProps);
      }
      if (!!callback) {
        callback(null, e);
      }
    }
    return responseJson;
  },

  // this function implements a "cache-first" policy for get response
  // Returns a cached response if one is found - only if one is not found, returns a response from the server
  getAnyResponse: function <T>(
    url: string,
    params: Record<string, string> | null | undefined,
    retryMode: RetryMode,
    callback: ((response: any, error: null) => void) | null
  ) {
    this.getCachedResponse(url, (error, payload) => {
      if (error || !payload) {
        try {
          console.debug('RS Cache: cache empty, attempt get server response: ' + url);
          this.getServerResponse(url, params, retryMode, callback);
        } catch (e) {
          console.debug('RS Cache: cache empty, get server response error:' + e + ' url: ' + url);
          DdLogs.warn('[RESTSTORE_CACHE_FALLBACK_ERROR]', {
            url,
            errorMessage: getErrorMessage(e),
            errorCode: (e as any)?.code ?? null,
            timestamp: new Date().toISOString(),
          });
        }
      } else {
        console.debug('RS Cache: cache found: ' + url);
        if (callback != null) {
          callback(payload, null);
        }
      }
    });
  },

  // this function implements a "from all available sources" policy for get response
  // Returns a cached response (if one is found) and always returns a response from the server
  // Therefore the caller-supllied callback is often called twice - once with a cached response and again
  // with the server response
  // This behavior is often used when a screen first loads - by populating it quickly with potentially
  // stale data while waiting for up-to-date information from the server
  getCacheAndServerResponse: function (url, params, retryMode, callback) {
    this.getCachedResponse(url, (error, response) => {
      // we don't want getCachedResponse to report cache misses (by invoking callback(null))
      if (response && callback) {
        // directly to our caller like it ordinarily does - so we suppress them here
        callback(response);
      }
    });
    this.getServerResponse(url, params, retryMode, callback);
  },

  _cacheNameForURL: function (url) {
    return CACHE_PREFIX + url;
  },

  clearCachedResponse: async function (url) {
    console.debug('RS Cache: clear cache for: ' + this._cacheNameForURL(url));
    await AsyncStorage.removeItem(this._cacheNameForURL(url));
  },

  /**
   * Clear all cached responses that start with a given URL prefix.
   * This handles cache key mismatches where URLs may have query params.
   * e.g., 'tournament_categories' clears 'tournament_categories/', 'tournament_categories/?new_user_path=X', etc.
   */
  clearCachedResponseByPrefix: async function (urlPrefix: string) {
    try {
      const allKeys = await AsyncStorage.getAllKeys();
      const cachePrefix = this._cacheNameForURL(urlPrefix);
      const keysToRemove = allKeys.filter((key) => key.startsWith(cachePrefix));

      if (keysToRemove.length > 0) {
        await AsyncStorage.multiRemove(keysToRemove);
        DdLogs.info('[RESTSTORE_CACHE_CLEARED_BY_PREFIX]', {
          urlPrefix,
          keysRemoved: keysToRemove.length,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (e) {
      DdLogs.warn('[RESTSTORE_CACHE_CLEAR_PREFIX_ERROR]', {
        urlPrefix,
        errorMessage: getErrorMessage(e),
        errorCode: (e as any)?.code ?? null,
        timestamp: new Date().toISOString(),
      });
    }
  },

  clearMatchStreamCache(userId) {
    this._data = null;
    this.clearCachedResponse('users/' + userId + '/matches');
  },

  clearEntireCache: function (url) {
    AsyncStorage.getAllKeys().then((allKeys) => {
      const skillzKeys = [];
      _.forEach(allKeys, (key) => {
        if (_.includes(key, CACHE_PREFIX)) {
          skillzKeys.push(key);
        }
      });
      console.debug('RS Cache: Cache clear skillzKeys: ' + JSON.stringify(skillzKeys));
      console.debug(
        'RS Cache: Cache clear skillz url keys: ' +
          skillzKeys.length +
          ', all keys: ' +
          allKeys.length
      );

      AsyncStorage.multiRemove(skillzKeys, (err) => {
        if (err) {
          console.debug('RS Cache: Cache clear failed: ' + err);
        } else {
          console.debug('RS Cache: Cache clear success');
        }
      });
    });
  },

  _getMessageForError(error: Object) {
    const errorText = _.get(error, '_bodyInit');

    if (errorText) {
      try {
        const jsonError = JSON.parse(errorText);
        const errorMessage = _.get(jsonError[0], 'message');
        console.debug('RESTStore: Successfully parsed error message: ' + errorMessage);
        return errorMessage;
      } catch (e) {
        console.debug('RESTStore: Failed to parse message from: ' + JSON.stringify(error));
      }
    }

    return null;
  },
};

export {RESTStore as default};
